<template>
  <slot name="title" :contacts-count="paginatorInfo?.total" :refetch="refetch" />

  <div v-if="contactsLoadingError" class="tw-mt-9">
    <ContentLoadingErrorNotice />
  </div>
  <template v-else>
    <div class="card card-white">
      <AppliedContactsList
        :additional-columns="additionalColumns"
        :loading="loading"
        :entries="entries"
        :paginator-info="paginatorInfo"
        :multi-select="multiSelect"
        :pre-selected-rows="preSelectedRows"
        @search="search"
        @row-click="emit('row-click', $event)"
        @rows-selected="emit('rows-selected', $event)"
      >
        <template v-for="name in Object.keys($slots).filter(filterSlots)" #[name]="slotProps">
          <slot :name="name" :refetch="refetch" v-bind="slotProps || {}"></slot>
        </template>
        <template #control-bar>
          <AppliedContactsControlBar
            :default-page-length="DEFAULT_PAGE_LENGTH"
            @search="searchInput"
            @sort="sort"
            @update-filters="updateFilters"
          />
        </template>
      </AppliedContactsList>
    </div>
    <!-- Use this slot as a provider, allowing you to perform actions needing to refresh after-->
    <slot name="actions" :refetch="refetch" />
  </template>
</template>

<script setup lang="ts">
import { computed, ref, Ref } from 'vue'
import { useDebounceFn } from '@vueuse/core/index'
import SilentNetworkError from '@/utils/notification/SilentNetworkError'
import ContentLoadingErrorNotice from '@/components/ContentLoadingErrorNotice.vue'

import { TrustedContactsList as CompanyTrustedContactsList } from '@/components/Contacts/CompanyFreelancer/queries/TrustedContacts'
import useTrustedContactsList, {
  TrustedContactListVariables,
} from '@/components/Contacts/CompanyFreelancer/composables/trustedContactsList'
import { TrustedContact } from '@/components/Job/hiring-types'
import { SortOrder, TrustedContactOrderByColumn } from '@/gql/graphql'
import AppliedContactsList from '@/components/Contacts/CompanyFreelancer/PageApplicants/AppliedContactsList.vue'
import AppliedContactsControlBar from '@/components/Contacts/CompanyFreelancer/PageApplicants/AppliedContactsControlBar.vue'

interface Props {
  multiSelect?: boolean
  preSelectedTrustedContacts?: TrustedContact[]
  additionalColumns?: object[]
}

const props = withDefaults(defineProps<Props>(), {
  multiSelect: false,
  preSelectedTrustedContacts: () => [],
  additionalColumns: () => [],
})

const emit = defineEmits(['rows-selected', 'row-click', 'search'])

const contactsLoadingError = ref(false)

const DEFAULT_PAGE_LENGTH = 50

const columnSortMapping = {
  freelancer: TrustedContactOrderByColumn.WorkerName,
  countryColumn: TrustedContactOrderByColumn.WorkerCountry,
  businessSetup: TrustedContactOrderByColumn.WorkerBusinessSetup,
}

const queryVariables: Ref<TrustedContactListVariables> = ref({
  accounts: [currentAccount.globalId],
  allAccounts: [currentAccount.globalId],
  statuses: ['APPLIED'],
  page: 1,
  perPage: DEFAULT_PAGE_LENGTH,
  search: null,
  origin: null,
  orderBy: [
    {
      column: columnSortMapping.freelancer,
      order: SortOrder.Asc,
    },
  ],
  markets: [],
  invitedByUsers: [],
  skills: [],
  companyId: currentAccount.globalId,
  hireHistory: [],
  hireStatus: [],
  staffingAgencyStatus: null,
})

const {
  trustedContacts: entries,
  rawResult,
  loading,
  refetch,
  onError: onContactsLoadingError,
} = useTrustedContactsList(CompanyTrustedContactsList, queryVariables)

onContactsLoadingError((error) => {
  contactsLoadingError.value = true

  new SilentNetworkError({
    ex: error,
  }).dispatch()
})

const preSelectedRows = computed(() =>
  entries.value.filter((entry) =>
    props.preSelectedTrustedContacts.find(
      (trustedContact) => trustedContact.workerId === entry.workerId,
    ),
  ),
)

const paginatorInfo = computed(() => rawResult.value?.trustedContacts?.paginatorInfo)

const updateFilters = (filters) => {
  queryVariables.value = {
    ...queryVariables.value,
    ...filters,
    // Reset page when filtering so that resulting records will display
    page: 0,
  }
}

const searchInput = useDebounceFn((search) => {
  queryVariables.value = {
    ...queryVariables.value,
    // Reset page when searching so that resulting records will display
    page: 0,
    search,
  }
}, 600)

const search = (values) => {
  queryVariables.value = {
    ...queryVariables.value,
    page: values.page + 1,
    perPage: values.perPage,
  }
}

const sort = (orderBy) => {
  queryVariables.value = {
    ...queryVariables.value,
    orderBy,
  }
}

const filterSlots = (slotName: string) => !['control-bar'].includes(slotName)
</script>
